import { useState } from 'react';
import type { JSX } from 'react';
import type { JournalEntry, JournalQuestData } from '@roll1/shared';
import type { TranslationKeys } from '../../i18n/types.js';
import { questChildren, questDescendantIds, questParent } from '../../lib/questChain';
import { beatDisplayName } from './ownerPlace.js';
import { SectionHeader } from '../ui/section-header';
import { Pill } from '../ui/pill';
import { FIELD_CLASS } from '../ui/field';

/**
 * Pieces of a quest in Studio (mockup gm-studio-quests-preview.html: questCard(), chainHtml(), renderQuest()):
 * the status and the giver on the card, the chain «follows from» / «next», and «Where in the plan».
 * Colours are Studio's own (neutral-*, amber-400), as the rail and the beat panel.
 */

type Translate = (key: keyof TranslationKeys) => string;

const questData = (quest: JournalEntry): JournalQuestData => quest.data as JournalQuestData;

const linkButton = 'text-hl hover:underline';

/** Status pill and «from <giver>» (a button: opens the NPC). */
export function QuestCardHead(props: {
  readonly quest: JournalEntry;
  readonly entries: readonly JournalEntry[];
  readonly onOpenEntry: (entryId: string) => void;
  readonly t: Translate;
}): JSX.Element {
  const { quest, entries, onOpenEntry, t } = props;
  const data = questData(quest);
  const giver = data.questGiverEntryId !== undefined ? entries.find((e) => e.id === data.questGiverEntryId) : undefined;
  return (
    <>
      <Pill tone="plain" className="flex-none" data-testid="quest-status-pill">
        {t(`journal.form.questStatus.${data.status}` as keyof TranslationKeys)}
      </Pill>
      {giver !== undefined ? (
        <button
          type="button"
          className="min-w-0 truncate text-meta text-muted-foreground hover:text-hl hover:underline"
          data-testid="quest-giver"
          title={t('journal.quest.from').replace('{name}', giver.name)}
          onClick={(): void => onOpenEntry(giver.id)}
        >
          {t('journal.quest.from').replace('{name}', giver.name)}
        </button>
      ) : null}
    </>
  );
}

/** «Follows from [parent] after objective “…”» and/or «Next: [child], …» — names are buttons that open the quest. */
export function QuestChainLine(props: {
  readonly quest: JournalEntry;
  readonly entries: readonly JournalEntry[];
  readonly onOpenQuest: (questId: string) => void;
  readonly t: Translate;
  /** The selects below say the parent themselves: the line then keeps to «Next:». */
  readonly hideParent?: boolean;
  /** «Change»: opens the selects. */
  readonly onChange?: () => void;
}): JSX.Element | null {
  const { quest, entries, onOpenQuest, t, hideParent = false, onChange } = props;
  const parent = hideParent ? null : questParent(entries, quest);
  const children = questChildren(entries, quest.id);
  if (parent === null && children.length === 0) return null;
  return (
    <div
      className="flex flex-wrap items-center gap-x-3 gap-y-1 border-y border-line px-3 py-2 text-meta text-muted-foreground"
      data-testid="quest-chain"
    >
      {parent !== null ? (
        <span>
          {t('journal.chain.followsFrom')}{' '}
          <button type="button" className={linkButton} onClick={(): void => onOpenQuest(parent.quest.id)}>
            {parent.quest.name}
          </button>
          {parent.objective !== undefined ? (
            <>
              {' '}
              <span>{t('journal.chain.afterObjective').replace('{name}', parent.objective.text)}</span>
            </>
          ) : null}
        </span>
      ) : null}
      {parent !== null && onChange !== undefined ? (
        <button type="button" className="text-dim hover:text-fg hover:underline" onClick={onChange}>
          {t('journal.chain.change')}
        </button>
      ) : null}
      {children.length > 0 ? (
        <span>
          {t('journal.chain.next')}{' '}
          {children.map((child, index) => (
            <span key={child.id}>
              {index > 0 ? ', ' : ''}
              <button type="button" className={linkButton} onClick={(): void => onOpenQuest(child.id)}>
                {child.name}
              </button>
            </span>
          ))}
        </span>
      ) : null}
    </div>
  );
}

interface PlanBeat {
  readonly id: string;
  readonly order: number;
  readonly name?: string | null;
  readonly sessionLabel?: string | null;
  readonly materials?: readonly { readonly kind: string; readonly id: string }[];
  readonly journalEntryId?: string | null;
}

/** The beats that hold the quest: in their materials, or as their prep text. */
export function beatsHoldingQuest(beats: readonly PlanBeat[], questId: string): PlanBeat[] {
  return [...beats]
    .sort((a, b) => a.order - b.order)
    .filter(
      (beat) =>
        (beat.materials ?? []).some((m) => m.kind === 'journal' && m.id === questId) || beat.journalEntryId === questId,
    );
}

/** «Where in the plan»: one button per beat that holds the quest; a click selects the beat. */
export function QuestWherePlan(props: {
  readonly questId: string;
  readonly beats: readonly PlanBeat[];
  readonly onSelectBeat: (beatId: string) => void;
  readonly t: Translate;
}): JSX.Element {
  const { questId, beats, onSelectBeat, t } = props;
  const holding = beatsHoldingQuest(beats, questId);
  return (
    <section className="px-3 pb-3 pt-2" data-testid="quest-where-plan">
      <SectionHeader as="h2" className="pane-section-head flex items-center" data-section-head="">
        {t('studio.quest.wherePlan')}
      </SectionHeader>
      {holding.length === 0 ? (
        <p className="text-meta text-dim">{t('studio.quest.notInPlan')}</p>
      ) : (
        <div className="flex flex-wrap gap-2">
          {holding.map((beat) => {
            const named = beatDisplayName(beat, t);
            return (
              <button
                key={beat.id}
                type="button"
                className="flex h-7 min-w-0 max-w-full items-center gap-2 rounded-md border border-line bg-panel-2 px-2.5 text-left text-body-s text-fg hover:border-hl/50"
                title={named}
                onClick={(): void => onSelectBeat(beat.id)}
              >
                <span className="text-meta text-dim">{`${t('studio.beat.one')} ${beat.order}`}</span>
                <span className="min-w-0 truncate">{named === `${t('studio.beat.one')} ${beat.order}` ? '' : named}</span>
              </button>
            );
          })}
        </div>
      )}
    </section>
  );
}

const nativeSelect = `h-7 ${FIELD_CLASS}`;

/**
 * Q-5: the chain is set where the quest is looked at (Studio, view mode — like the entry's «Links»): «Follows from»
 * (the other quests; «—» clears) and, once a quest is chosen, «after objective» (that quest's objectives;
 * «any objective» = no objective). A quest that comes after this one is not offered (the server refuses a cycle).
 */
export function QuestChainEditor(props: {
  readonly quest: JournalEntry;
  readonly entries: readonly JournalEntry[];
  readonly onChange: (followsFrom: { questId: string; objectiveId?: string } | null) => void;
  readonly t: Translate;
}): JSX.Element {
  const { quest, entries, onChange, t } = props;
  const follows = questData(quest).followsFrom ?? null;
  const after = questDescendantIds(entries, quest.id);
  const choices = entries.filter((e) => e.type === 'quest' && e.id !== quest.id && !after.has(e.id));
  const parent = follows !== null ? entries.find((e) => e.id === follows.questId && e.type === 'quest') : undefined;
  const objectives = parent !== undefined ? (questData(parent).objectives ?? []) : [];
  return (
    <div className="flex flex-wrap items-center gap-x-3 gap-y-1.5 px-3 py-2 text-meta text-muted-foreground" data-testid="quest-chain-editor">
      <label htmlFor={`chain-from-${quest.id}`} className="flex-none">
        {t('journal.form.quest.followsFrom')}
      </label>
      <select
        id={`chain-from-${quest.id}`}
        className={nativeSelect}
        value={follows?.questId ?? ''}
        onChange={(e): void => onChange(e.target.value === '' ? null : { questId: e.target.value })}
      >
        <option value="">—</option>
        {choices.map((e) => (
          <option key={e.id} value={e.id}>
            {e.name}
          </option>
        ))}
      </select>
      {follows !== null && parent !== undefined ? (
        <>
          <label htmlFor={`chain-after-${quest.id}`} className="flex-none">
            {t('journal.form.quest.followsObjective')}
          </label>
          <select
            id={`chain-after-${quest.id}`}
            className={nativeSelect}
            value={follows.objectiveId ?? ''}
            onChange={(e): void =>
              onChange(e.target.value === '' ? { questId: follows.questId } : { questId: follows.questId, objectiveId: e.target.value })
            }
          >
            <option value="">{t('journal.form.quest.followsAnyObjective')}</option>
            {objectives.map((o) => (
              <option key={o.id} value={o.id}>
                {o.text}
              </option>
            ))}
          </select>
        </>
      ) : null}
    </div>
  );
}

/**
 * The quest view's chain: the text line («Follows from [parent] after objective …», «Next: …») with «Change»; the
 * selects only open when there is no parent yet or «Change» is pressed (the mockup shows the text; setting the
 * chain is an edit).
 */
export function QuestChainRow(props: {
  readonly quest: JournalEntry;
  readonly entries: readonly JournalEntry[];
  readonly onOpenQuest: (questId: string) => void;
  readonly onChange: (followsFrom: { questId: string; objectiveId?: string } | null) => void;
  readonly t: Translate;
}): JSX.Element {
  const { quest, entries, onOpenQuest, onChange, t } = props;
  const [editing, setEditing] = useState<boolean>(false);
  const hasChain = questParent(entries, quest) !== null || questChildren(entries, quest.id).length > 0;
  return (
    <>
      <QuestChainLine
        quest={quest}
        entries={entries}
        onOpenQuest={onOpenQuest}
        t={t}
        hideParent={editing}
        onChange={(): void => setEditing(true)}
      />
      {/* No chain yet (mockup quests__quest-q1): nothing but a quiet way to set one. */}
      {!hasChain && !editing ? (
        <div className="px-3 py-1.5 text-meta">
          <button type="button" className="text-dim hover:text-hl hover:underline" data-testid="quest-chain-set" onClick={(): void => setEditing(true)}>
            {t('journal.form.quest.followsFrom')}…
          </button>
        </div>
      ) : null}
      {editing ? <QuestChainEditor quest={quest} entries={entries} onChange={onChange} t={t} /> : null}
    </>
  );
}
